import { AppShell, NavLink, ScrollArea, Stack } from "@mantine/core";
import { createElement } from "react";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";
import classes from "@mountayaapp/theme/railNavLink.module.css";

import { normalizeHref } from "../content/href";
import { getNavManifest, type Tab } from "../content/registry";
import { resolveLanguage } from "../locale/resolveLanguage";
import { useDocsPathname } from "./DocsPathnameContext";

/**
 * DocsNavbarProps carry the active tab whose curated groups the sidebar lists. The
 * shell renders the navbar only for tabs whose layout is `sidebar`, and the tab id
 * filters the manifest so the rail shows that tab's sections alone.
 */
interface DocsNavbarProps {
  tab: Tab;
}

/**
 * DocsNavbar is the docs sidebar: a scrollable rail of the active tab's curated
 * navigation groups, headed by the pinned entry section the manifest returns for
 * every tab, so the way into the platform is present wherever a reader lands. Each
 * group is a collapsible section headed by its icon and localized label, expanded
 * by default when it holds the active page, with its pages hanging off a guide rule
 * as full-reload anchors that mark the current one — the docs app serves static
 * pages with no client router, so links are plain anchors. The brand, theme switch,
 * and Map Studio link live in the persistent header rather than here, so the navbar
 * is just the navigation.
 *
 * Its box, density, and item states come from the shared rail chrome in the theme, the
 * same rules the application's account and organization rail renders under, so the two
 * page rails read as one surface.
 */
export const DocsNavbar = ({ tab }: DocsNavbarProps) => {
  const { t } = useTranslation("docs");
  const pathname = useDocsPathname();
  const language = resolveLanguage(pathname);
  const groups = getNavManifest(language, tab.id);
  const current = normalizeHref(pathname);

  return (
    // The region AppShell reserves is already a <nav>, so the list inside it is a plain
    // Stack: a second navigation landmark nested in the first names nothing new and
    // leaves a screen-reader user two identical, unlabelled entries where the page has
    // one rail. The label is what tells that one entry apart from the header's tab row
    // and the reference rail, which name themselves the same way.
    <AppShell.Navbar aria-label={t("nav.pages")} className={classes.rail}>
      <AppShell.Section component={ScrollArea} grow type="scroll">
        <Stack gap="md">
          {groups.map((group) => {
            // The group opens by default when it holds the active page, so the
            // current link is always visible without the reader hunting for it.
            const isActiveGroup = group.items.some((item) => normalizeHref(item.href) === current);

            return (
              <NavLink
                aria-label={t("nav.toggleGroup", { group: t(group.labelKey) })}
                childrenOffset={0}
                classNames={{ children: classes.children, root: classes.section }}
                defaultOpened={isActiveGroup}
                fw={600}
                key={group.labelKey}
                label={t(group.labelKey)}
                leftSection={createElement(group.icon, { size: ICON_SIZE.sm })}
              >
                {group.items.map((item) => (
                  <NavLink
                    active={normalizeHref(item.href) === current}
                    classNames={{ root: classes.item }}
                    component="a"
                    href={item.href}
                    key={item.href}
                    label={item.title}
                  />
                ))}
              </NavLink>
            );
          })}
        </Stack>
      </AppShell.Section>
    </AppShell.Navbar>
  );
};
